import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import styled from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Button, { BUTTON_STYLES } from "components/atoms/button";
import SelectBox from "components/atoms/input/SelectBox";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import ReferenceLayer from "components/molecules/ReferenceLayer";
import AbstractModal from "components/page/modal/AbstractModal";

import { useReferenceList } from "hooks/queries/useReference";
import { useSelectBoxOptions } from "hooks/queries/useOptions";
import { useReferenceAnalysis, useReferenceAnalysisDelete } from "hooks/queries/useResearch";
import { useUser } from "hooks/queries/useUser";

import { getJsonToBib } from "utils/request/reference";
import { apiSpaceUrl, wsUrl } from "utils/urls";
import { isFreePlanUser, isPaidPlanUser } from "utils/user";
import { uuidv4 } from "utils/uuid";

import axios from "axios";
import saveAs from "file-saver";

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const ContentsWrap = styled.div`
    display: flex;
    flex: 1;
    margin: 0;
    min-height: 0;
`;

const MainContentSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
`;

const AnalysisSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
    min-height: 200px;
`;

const ReferenceSection = styled.div`
    height: 180px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
`;

const RelParagraph = styled.div`
    margin: 0;
    padding: 16px;
    width: 100%;
    flex: 1;
    border: 1px solid var(--color-Line);
    border-radius: 4px;
    background-color: var(--color-White);
    text-align: justify;
    line-height: 1.4;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-SubBlack);
    white-space: pre-wrap;
    overflow-y: auto;
    min-height: 0;
`;

const TabViewSection = styled.div`
    margin: 8px 0 0 0;
    width: 100%;
    flex: 1;
    overflow: auto;
    min-height: 0;
`;

const ReferenceView = styled.div`
    width: 100%;
    height: 100%;
`;

const HistoryContent = styled.div`
    padding: 16px;
    height: calc(100% - 60px);
    overflow-y: auto;
    min-height: 0;
`;

const TabHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-top: 16px;
`;

const LeftSection = styled.div`
    display: flex;
    align-items: center;
`;

const RightSection = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ReferenceListWrap = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: auto;
`;

const RefListItemWrap = styled.div`
    padding: 8px 0;
    width: 100%;
    border-bottom: dashed 1px var(--color-Line);
`;

const RefCitationText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})``;

const AnalysisHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 40px;
`;

const HeaderText = styled(Text)`
    font-weight: 500;
    font-size: 1.4rem;
`;

const AnalysisHeaderButtonWrap = styled.div`
    display: flex;
    align-items: center;
`;

const HistorySection = styled.div`
    width: 300px;
    overflow: hidden;
    border-left: 1px solid var(--color-Line);
    background-color: var(--color-Background, #fafafa);
    margin-left: 16px;
`;

const HistoryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid var(--color-Line);
    background-color: var(--color-White);
`;

const HistoryItem = styled.div`
    padding: 12px;
    margin-bottom: 8px;
    background-color: ${(props) => (props.$isSelected ? "var(--color-BackgroundHover)" : "var(--color-White)")};
    border: ${(props) => (props.$isSelected ? "2px" : "1px")} solid
        ${(props) => (props.$isSelected ? "var(--color-ResearchActive)" : "var(--color-Line)")};
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: var(--color-BackgroundHover, #f5f5f5);
        border-color: var(--color-BorderHover, #d0d0d0);
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const HistoryItemTitle = styled.div`
    font-size: 1.2rem;
    font-weight: 500;
    color: var(--color-Text, #333);
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const HistoryItemPreview = styled.div`
    font-size: 1.1rem;
    color: var(--color-SubBlack, #555);
    margin-top: 8px;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const SubscriptionBlocker = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    padding: 40px;
`;

const BlockerTitle = styled(HeaderText)`
    margin-bottom: 12px;
    color: var(--color-Text, #333);
`;

const BlockerDescription = styled(Text)`
    text-align: center;
    margin-bottom: 24px;
    color: var(--color-Grey1);
    font-size: 1.4rem;
`;

const Divider = styled.div`
    width: 1px;
    height: 16px;
    background-color: var(--color-Line);
    margin: 0 8px;
`;

function ResearchAnalysisModal(props) {
    const analysisMode = props.analysisMode || "create";
    const [allReferences, setAllReferences] = useState([]);
    const [initial, setInitial] = useState(true);

    const { space_uuid } = useParams();

    const [language, setLanguage] = useState("");
    const [languageOptions, setLanguageOptions] = useState([]);

    const [style, setStyle] = useState(props.research.citation_style);
    const [styleList, setStyleList] = useState([]);
    const [styledTexts, setStyledTexts] = useState([]);

    const userQuery = useUser();
    const optionsQuery = useSelectBoxOptions();
    const referencesQuery = useReferenceList({
        enabled: true,
    });

    const analysisQuery = useReferenceAnalysis({
        research_id: props.research.id,
    });
    const { data: analysisData, refetch: refetchAnalysis, remove: removeAnalysis } = analysisQuery;
    const analysisDeleteMutation = useReferenceAnalysisDelete();

    const [analysisHistory, setAnalysisHistory] = useState([]);
    const [selectedHistoryId, setSelectedHistoryId] = useState(null);

    const wsRef = useRef(null);

    const { setToast } = useToast();
    const { setLoading } = useLoading();

    useEffect(() => {
        if (userQuery.data) {
            setLanguage(userQuery.data.env_ai_language);
        }
        if (optionsQuery.data) {
            setLanguageOptions(optionsQuery.data.env_ai_language);
            setStyleList(optionsQuery.data.env_citation_style);
        }
        if (referencesQuery.data) {
            setAllReferences(referencesQuery.data);
        }
    }, [userQuery.data, optionsQuery.data, referencesQuery.data]);

    useEffect(() => {
        if (analysisData) {
            setAnalysisHistory(analysisData);
        }
    }, [analysisData]);

    useEffect(() => {
        if (props.research) {
            setLanguage(props.research.ai_response_language);
        }
    }, [props.research]);

    const createAnalysis = useCallback(() => {
        setLoading(true);
        wsRef.current = new WebSocket(wsUrl("researchAnalysisConsumer"));

        wsRef.current.onerror = (error) => {
            console.log(error);
        };

        wsRef.current.onopen = () => {
            wsRef.current.send(
                JSON.stringify({
                    action: "analyze",
                    language: language,
                    research_id: props.research.id,
                    reference_uuids: props.references.map((reference) => reference.uuid),
                    space_uuid: space_uuid,
                }),
            );
        };

        wsRef.current.onmessage = (evt) => {
            const data = JSON.parse(evt.data);
            // 연구노트 설명에 데이터를 더한다.
            if (data.content === "<START>") {
                setLoading(false);
            } else if (data.content === "<END>") {
                wsRef.current.close();
            } else if (data.content.id) {
                setAnalysisHistory((prev) => [{ ...data.content, isAnalyzing: true }, ...prev]);
            } else {
                setAnalysisHistory((prev) => {
                    prev[0].result += data.content;
                    return [...prev];
                });
            }
        };

        wsRef.current.onclose = (event) => {
            setLoading(false);
            wsRef.current = null;
            refetchAnalysis();
            if (event.code !== 1000) {
                // 정상 종료가 아닌 경우
                console.log(event);
                Alert("warn", "에러", "연결이 끊겼습니다.\n잠시 후 새로고침 해주세요.");
            }
        };
    }, [language, props.references, props.research.id, refetchAnalysis, setLoading, space_uuid]);

    useEffect(() => {
        if (analysisMode === "create" && language && initial && isPaidPlanUser(props.user)) {
            createAnalysis();
            setInitial(false);
        }
    }, [analysisMode, createAnalysis, initial, language, props.user]);

    useEffect(() => {
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
            setInitial(true);
            setAnalysisHistory([]);
            setSelectedHistoryId(null);
            setStyledTexts([]);
            removeAnalysis();
        };
    }, [removeAnalysis]);

    const selectedAnalysisHistory = analysisHistory.find((history) => history.id === selectedHistoryId);
    const latestAnalysisHistoryId = analysisHistory[0]?.id;

    useEffect(() => {
        if (latestAnalysisHistoryId) {
            if (isFreePlanUser(props.user)) {
                return;
            }
            setSelectedHistoryId(latestAnalysisHistoryId);
        }
    }, [latestAnalysisHistoryId, props.user]);

    const handleCopyButtonClick = () => {
        navigator.clipboard
            .writeText(styledTexts.join("\n"))
            .then(() => {
                setToast("복사되었습니다.", "info");
            })
            .catch((error) => {
                console.error("복사 실패:", error);
            });
    };

    const handleDownloadBib = () => {
        getJsonToBib(
            space_uuid,
            analysisHistory.find((history) => history.id === selectedHistoryId).bibliography_bib_json,
        ).then((res) => {
            if (res.bibs) {
                saveAs(new Blob([res.bibs.join("\n")]), `paper-${new Date().toISOString()}.bib`);
            } else {
                Alert("warn", "에러", "BibTeX 변환에 실패했습니다.");
            }
        });
    };

    const handleChangeStyle = useCallback(
        (style, bib_json) => {
            setLoading(true);
            axios
                .post(apiSpaceUrl("referenceStyle", space_uuid), {
                    style_type: style,
                    ...(bib_json && { bib_json: bib_json }),
                })
                .then((res) => {
                    setLoading(false);
                    setStyle(style);
                    setStyledTexts(res.data);
                })
                .catch((err) => {
                    const errorMessage =
                        err.response?.data?.bib_json ||
                        err.response?.data?.detail ||
                        "참고문헌 스타일 변경에 실패했습니다.";

                    console.error("참고문헌 스타일 변경 실패", {
                        error: err,
                        status: err.response?.status,
                        response: err.response?.data,
                        style,
                        referenceCount: bib_json?.length || 0,
                    });
                    setLoading(false);
                    Alert("error", "에러", errorMessage);
                });
        },
        [setLoading, space_uuid],
    );

    useEffect(() => {
        if (!selectedAnalysisHistory?.bibliography_bib_json || selectedAnalysisHistory.isAnalyzing) {
            return;
        }

        handleChangeStyle(style, selectedAnalysisHistory.bibliography_bib_json);
    }, [handleChangeStyle, selectedAnalysisHistory, style]);

    const stripSpans = (html) => {
        return html.replace(/<span[^>]*>(\[\d+\])<\/span>/g, "$1");
    };

    const renderSummaryText = () => {
        return stripSpans(analysisHistory.find((history) => history.id === selectedHistoryId)?.result || "")
            .split(/(\[\d+\])/g)
            .map((chunk, idx) => {
                const match = chunk.match(/\[(\d+)\]/);
                if (match) {
                    const index = match[1];
                    const paper = analysisHistory.find((history) => history.id === selectedHistoryId)
                        .bibliography_bib_json[Number(index) - 1];
                    return (
                        <ReferenceLayer key={idx} reference={paper}>
                            [{index}]
                        </ReferenceLayer>
                    );
                } else {
                    return <span key={idx}>{chunk}</span>;
                }
            });
    };

    const handleAddAnalysisToEditor = () => {
        setLoading(true);
        const quill = props.editorRef.current.getEditor();
        quill.focus();

        const range = quill.getSelection();
        let plainText = "\n" + analysisHistory.find((history) => history.id === selectedHistoryId)?.result || "";

        const referencePattern = /\[(\d+)\]/g;
        let match;
        let references = [];

        while ((match = referencePattern.exec(plainText)) !== null) {
            const refId = match[1];
            const uuid = uuidv4(); // front_uuid로 사용할 UUID 생성
            references.push({
                uuid: allReferences.find(
                    (ref) =>
                        ref.bib_json.citation_key ===
                        analysisHistory.find((history) => history.id === selectedHistoryId)?.bibliography_bib_json[
                            refId - 1
                        ]?.citation_key,
                )?.uuid,
                front_uuid: uuid,
                match: match[0], // 나중에 일치한 부분을 고유하게 식별하기 위해 사용
                index: match.index, // 일치한 인용문의 위치를 기억
            });
            plainText = plainText.replace(match[0], `[${uuid}]`); // UUID로 대체
        }

        if (references.length > 0) {
            // 인용된 참고문헌이 있는 경우
            props.referenceCitationMutation.mutate(
                {
                    research_id: props.research.id,
                    references: references,
                },
                {
                    onSuccess: (response) => {
                        let htmlContent = plainText;
                        references.forEach((ref) => {
                            const citation = response.citations.find(
                                (cit) => cit.reference_front_uuid === ref.front_uuid,
                            );
                            const text = response.research.cited_references?.find(
                                (reference) => reference.uuid === ref.uuid,
                            )?.citation_phrase;

                            if (citation) {
                                const spanTag = `<span class="ql-editor-reference-blot" data-reference="${ref.uuid}" data-id="${citation.id}" data-front-uuid="${ref.front_uuid}">${text}</span>`;
                                htmlContent = htmlContent.replace(`[${ref.front_uuid}]`, spanTag);
                            }
                        });
                        // <br> 태그로 변환
                        htmlContent = htmlContent.replace(/\n/g, "<br/>");
                        const beforeLength = quill.getLength();

                        // 생성된 HTML 내용을 Quill 에디터에 삽입
                        quill.clipboard.dangerouslyPasteHTML(range.index, htmlContent, "api");

                        // 업데이트된 내용을 저장
                        props.updateResearchMutation.mutate(
                            {
                                id: props.research.id,
                                html_content: quill.root.innerHTML,
                            },
                            {
                                onSuccess: () => {
                                    const insertedLength = quill.getLength() - beforeLength;
                                    quill.setSelection(range.index + 1, insertedLength - 1, "api");
                                    props.exitModal();
                                    props.exitReferenceSidebar();
                                    setToast("노트에 추가되었습니다.", "info");
                                    setLoading(false);
                                },
                                onError: (error) => {
                                    setLoading(false);
                                    Alert("error", "에러", "서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.");
                                },
                            },
                        );
                    },
                    onError: (error) => {
                        setLoading(false);
                        Alert("error", "에러", "서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.");
                    },
                },
            );
        } else {
            // 인용된 참고문헌이 없는 경우
            let htmlContent = plainText;
            htmlContent = htmlContent.replace(/\n/g, "<br/>");
            const beforeLength = quill.getLength();

            // 생성된 HTML 내용을 Quill 에디터에 삽입
            quill.clipboard.dangerouslyPasteHTML(range.index, htmlContent, "api");

            // 업데이트된 내용을 저장
            props.updateResearchMutation.mutate(
                {
                    id: props.research.id,
                    html_content: quill.root.innerHTML,
                },
                {
                    onSuccess: () => {
                        const insertedLength = quill.getLength() - beforeLength;
                        quill.setSelection(range.index + 1, insertedLength - 1, "api");
                        props.exitModal();
                        props.exitReferenceSidebar();
                        setToast("노트에 추가되었습니다.", "info");
                        setLoading(false);
                    },
                    onError: (error) => {
                        setLoading(false);
                        Alert("error", "에러", "서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.");
                    },
                },
            );
        }
    };

    const checkCitationExist = () => {
        if (!analysisHistory.find((history) => history.id === selectedHistoryId)?.bibliography_bib_json) {
            return false;
        }

        for (const bib of analysisHistory.find((history) => history.id === selectedHistoryId)?.bibliography_bib_json) {
            const exists = allReferences.find((ref) => ref.bib_json.citation_key === bib.citation_key);
            if (!exists) {
                return false;
            }
        }
        return true;
    };

    return (
        <AbstractModal modalTitle="참고문헌 자동분석" width={900} exitModal={props.exitModal}>
            <Container>
                <ContentsWrap>
                    {isPaidPlanUser(props.user) ? (
                        <MainContentSection>
                            <AnalysisHeader>
                                <HeaderText>AI 분석 내용</HeaderText>
                                <AnalysisHeaderButtonWrap>
                                    {analysisHistory.find((history) => history.id === selectedHistoryId)
                                        ?.isAnalyzing ? (
                                        <Button
                                            {...BUTTON_STYLES.actionPrimary}
                                            width={"100px"}
                                            height={"26px"}
                                            buttonText={"작성 중지"}
                                            onClick={() => {
                                                wsRef.current.send(JSON.stringify({ action: "stop" }));
                                            }}
                                        >
                                            <Icon name={"stop"} color={"var(--color-White)"} />
                                        </Button>
                                    ) : (
                                        <>
                                            <Tooltip message={"클립보드에 복사"}>
                                                <Button
                                                    onlyIcon
                                                    bgColor={"var(--color-Button2)"}
                                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                                    margin={"0 8px"}
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(
                                                            analysisHistory.find(
                                                                (history) => history.id === selectedHistoryId,
                                                            )?.result,
                                                        );
                                                        setToast("복사되었습니다.", "info");
                                                    }}
                                                >
                                                    <Icon name={"copy"} size={"12"} color={"var(--color-White)"} />
                                                </Button>
                                            </Tooltip>

                                            {selectedHistoryId && checkCitationExist() && (
                                                <Tooltip message={"연구노트에 추가"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"var(--color-Button2)"}
                                                        hoverBgColor={"var(--color-ButtonHover2)"}
                                                        margin={"0 8px"}
                                                        onClick={() => handleAddAnalysisToEditor()}
                                                    >
                                                        <Icon name={"plus"} size={"12"} color={"var(--color-White)"} />
                                                    </Button>
                                                </Tooltip>
                                            )}

                                            {selectedHistoryId && selectedHistoryId === analysisHistory[0]?.id && (
                                                <>
                                                    <Divider />
                                                    <SelectBox
                                                        width="100px"
                                                        height="26px"
                                                        placeholder="언어 선택"
                                                        items={languageOptions}
                                                        value={language}
                                                        onChange={(e) => setLanguage(e)}
                                                    />
                                                    <Tooltip message={"다시 작성"}>
                                                        <Button
                                                            onlyIcon
                                                            bgColor={"var(--color-Button2)"}
                                                            hoverBgColor={"var(--color-ButtonHover2)"}
                                                            margin={"0 8px 0 2px"}
                                                            disabled={props.references.length === 0}
                                                            onClick={() => {
                                                                createAnalysis();
                                                            }}
                                                        >
                                                            <Icon
                                                                name={"refresh"}
                                                                size={"12"}
                                                                color={"var(--color-White)"}
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </>
                                    )}
                                </AnalysisHeaderButtonWrap>
                            </AnalysisHeader>
                            <AnalysisSection>
                                <RelParagraph>
                                    {renderSummaryText()}
                                    {analysisHistory.find((history) => history.id === selectedHistoryId)
                                        ?.isAnalyzing && <AiCursor />}
                                </RelParagraph>
                            </AnalysisSection>
                            <ReferenceSection>
                                <TabHeader>
                                    <LeftSection>
                                        <HeaderText>참고문헌</HeaderText>
                                    </LeftSection>
                                    <RightSection>
                                        <SelectBox
                                            width={"140px"}
                                            height={"32px"}
                                            placeholder="스타일"
                                            items={styleList}
                                            value={style}
                                            onChange={(e) => {
                                                if (selectedHistoryId) {
                                                    handleChangeStyle(
                                                        e,
                                                        analysisHistory.find(
                                                            (history) => history.id === selectedHistoryId,
                                                        )?.bibliography_bib_json,
                                                    );
                                                }
                                            }}
                                        />
                                        <Tooltip message={"클립보드에 복사"}>
                                            <Button
                                                onlyIcon
                                                bgColor={"var(--color-Button2)"}
                                                hoverBgColor={"var(--color-ButtonHover2)"}
                                                margin={"0 8px"}
                                                onClick={() => handleCopyButtonClick(styleList)}
                                            >
                                                <Icon name={"copy"} size={"12"} color={"var(--color-White)"} />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip message={"BibTeX 다운로드"}>
                                            <Button
                                                onlyIcon
                                                bgColor={"var(--color-Button2)"}
                                                hoverBgColor={"var(--color-ButtonHover2)"}
                                                onClick={() => handleDownloadBib()}
                                            >
                                                <Icon name={"download"} size={"12"} color={"var(--color-White)"} />
                                            </Button>
                                        </Tooltip>
                                    </RightSection>
                                </TabHeader>
                                <TabViewSection>
                                    <ReferenceView>
                                        <ReferenceListWrap>
                                            {styledTexts.map((styledText, index) => (
                                                <RefListItemWrap key={index}>
                                                    <RefCitationText>{styledText}</RefCitationText>
                                                </RefListItemWrap>
                                            ))}
                                        </ReferenceListWrap>
                                    </ReferenceView>
                                </TabViewSection>
                            </ReferenceSection>
                        </MainContentSection>
                    ) : (
                        <MainContentSection>
                            <AnalysisHeader>
                                <HeaderText>AI 분석 내용</HeaderText>
                                <AnalysisHeaderButtonWrap>
                                    {selectedHistoryId && (
                                        <>
                                            <Tooltip message={"클립보드에 복사"}>
                                                <Button
                                                    onlyIcon
                                                    bgColor={"var(--color-Button2)"}
                                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                                    margin={"0 8px"}
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(
                                                            analysisHistory.find(
                                                                (history) => history.id === selectedHistoryId,
                                                            )?.result,
                                                        );
                                                        setToast("복사되었습니다.", "info");
                                                    }}
                                                >
                                                    <Icon name={"copy"} size={"12"} color={"var(--color-White)"} />
                                                </Button>
                                            </Tooltip>
                                            {checkCitationExist() && (
                                                <Tooltip message={"연구노트에 추가"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"var(--color-Button2)"}
                                                        hoverBgColor={"var(--color-ButtonHover2)"}
                                                        margin={"0 8px"}
                                                        onClick={() => handleAddAnalysisToEditor()}
                                                    >
                                                        <Icon name={"plus"} size={"12"} color={"var(--color-White)"} />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                </AnalysisHeaderButtonWrap>
                            </AnalysisHeader>

                            {/* 구독 안내 영역 */}
                            {selectedHistoryId ? (
                                <>
                                    <AnalysisSection>
                                        <RelParagraph>{renderSummaryText()}</RelParagraph>
                                    </AnalysisSection>
                                    <ReferenceSection>
                                        <TabHeader>
                                            <LeftSection>
                                                <HeaderText>참고문헌</HeaderText>
                                            </LeftSection>
                                            <RightSection>
                                                <SelectBox
                                                    width={"140px"}
                                                    height={"32px"}
                                                    placeholder="스타일"
                                                    items={styleList}
                                                    value={style}
                                                    onChange={(e) =>
                                                        handleChangeStyle(
                                                            e,
                                                            analysisHistory.find(
                                                                (history) => history.id === selectedHistoryId,
                                                            )?.bibliography_bib_json,
                                                        )
                                                    }
                                                />
                                                <Tooltip message={"클립보드에 복사"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"var(--color-Button2)"}
                                                        hoverBgColor={"var(--color-ButtonHover2)"}
                                                        margin={"0 8px"}
                                                        onClick={() => handleCopyButtonClick()}
                                                    >
                                                        <Icon name={"copy"} size={"12"} color={"var(--color-White)"} />
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip message={"BibTeX 다운로드"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"var(--color-Button2)"}
                                                        hoverBgColor={"var(--color-ButtonHover2)"}
                                                        onClick={() => handleDownloadBib()}
                                                    >
                                                        <Icon
                                                            name={"download"}
                                                            size={"12"}
                                                            color={"var(--color-White)"}
                                                        />
                                                    </Button>
                                                </Tooltip>
                                            </RightSection>
                                        </TabHeader>
                                        <TabViewSection>
                                            <ReferenceView>
                                                <ReferenceListWrap>
                                                    {styledTexts.map((styledText, index) => (
                                                        <RefListItemWrap key={index}>
                                                            <RefCitationText>{styledText}</RefCitationText>
                                                        </RefListItemWrap>
                                                    ))}
                                                </ReferenceListWrap>
                                            </ReferenceView>
                                        </TabViewSection>
                                    </ReferenceSection>
                                </>
                            ) : (
                                <div style={{ flex: 1, display: "flex" }}>
                                    <SubscriptionBlocker>
                                        <BlockerTitle>유료 기능입니다</BlockerTitle>
                                        <BlockerDescription>
                                            유료 요금제로 업그레이드하여 더 많은 기능을 이용하세요.
                                        </BlockerDescription>
                                        <Button
                                            onlyText
                                            width={"180px"}
                                            height={"32px"}
                                            bgColor={"var(--color-Key)"}
                                            hoverBgColor={"var(--color-DarkKey)"}
                                            fontColor={"var(--color-White)"}
                                            buttonText={"요금제 업그레이드"}
                                            onClick={() => window.open("/plan", "_blank")}
                                        />
                                    </SubscriptionBlocker>
                                </div>
                            )}
                        </MainContentSection>
                    )}

                    {/* 새로 추가되는 히스토리 섹션 */}
                    <HistorySection>
                        <HistoryHeader>
                            <HeaderText>분석 히스토리</HeaderText>
                        </HistoryHeader>
                        <HistoryContent>
                            {analysisHistory?.length !== 0
                                ? analysisHistory.map((item, index) => (
                                      <HistoryItem
                                          key={index}
                                          onClick={() => {
                                              // 히스토리 아이템 클릭 시 해당 분석 내용을 로드하는 로직
                                              setSelectedHistoryId(item.id);
                                          }}
                                          $isSelected={selectedHistoryId === item.id}
                                      >
                                          <HistoryItemTitle>
                                              {item.created_at ? item.created_at : "분석 중.."}
                                              {item.created_at && (
                                                  <Tooltip message={"삭제"}>
                                                      <Button
                                                          onlyIcon
                                                          bgColor={"var(--color-Button4)"}
                                                          hoverBgColor={"var(--color-ButtonHover4)"}
                                                          fontColor={"var(--color-Black)"}
                                                          onClick={(e) => {
                                                              e.stopPropagation();
                                                              Confirm(
                                                                  "warn",
                                                                  "히스토리 삭제",
                                                                  "삭제하겠습니까?",
                                                                  "삭제",
                                                                  () => {
                                                                      analysisDeleteMutation.mutate(
                                                                          { history_id: item.id },
                                                                          {
                                                                              onSuccess: () => {
                                                                                  setToast("삭제되었습니다.", "info");
                                                                              },
                                                                              onError: () => {
                                                                                  Alert(
                                                                                      "error",
                                                                                      "에러",
                                                                                      "서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.",
                                                                                  );
                                                                              },
                                                                          },
                                                                      );
                                                                  },
                                                              );
                                                          }}
                                                      >
                                                          <Icon name="delete" size="12px" />
                                                      </Button>
                                                  </Tooltip>
                                              )}
                                          </HistoryItemTitle>
                                          <HistoryItemPreview>{item.result}</HistoryItemPreview>
                                      </HistoryItem>
                                  ))
                                : null}
                        </HistoryContent>
                    </HistorySection>
                </ContentsWrap>
            </Container>
        </AbstractModal>
    );
}

export default ResearchAnalysisModal;
